import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Reveal, useInView } from "../hooks";
import { PLATFORM_TOTALS } from "../content";

function IncludedTable({ stats }) {
  const [ref, inView] = useInView(0.15);
  const activeQuestions = stats?.practice_questions ?? PLATFORM_TOTALS.activeQuestions;
  const fullMocks = stats?.full_mocks ?? PLATFORM_TOTALS.fullMocks;
  const miniMocks = stats?.mini_mocks ?? 0;
  const studyGuides = stats?.study_guides ?? 11;

  const rows = [
    { feature: "Active Question Bank", detail: `${activeQuestions.toLocaleString()} original UCAT questions` },
    { feature: "Full Mock Exams", detail: `${fullMocks} included` },
    { feature: "Section-Specific Practice", detail: miniMocks > 0 ? `${miniMocks} mini mocks included` : "Timed section practice included" },
    { feature: "Exam-Accurate Interface", detail: "UCAT-style layout, timer and calculator" },
    { feature: "Per-Question Time Analysis", detail: "See where time is being lost" },
    { feature: "Percentile Score Tracking", detail: "Track relative performance over time" },
    { feature: "Bell Curve Score Distribution", detail: "Understand where scores sit" },
    { feature: "Section & Category Analytics", detail: "Breakdowns by UCAT section and skill" },
    { feature: "Official-Informed Study Guides", detail: `${studyGuides} public guides` },
    { feature: "Score Trajectory Over Time", detail: "Follow improvement across sessions" },
    {
      feature: "Data-Backed Study Plan",
      detail: "Use performance data to choose what to do next",
      highlight: true,
    },
  ];

  return (
    <div ref={ref} className="w-full overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "none" : "translateY(-12px)",
              transition: "opacity 0.6s ease, transform 0.6s ease",
            }}
          >
            <th className="text-left py-4 px-6 text-muted-foreground font-medium text-sm w-[44%] font-plex">
              Feature
            </th>
            <th className="text-left py-4 px-6 text-muted-foreground font-medium text-sm font-plex">
              What you get
            </th>
            <th className="py-4 px-6 text-center">
              <div className="inline-flex flex-col items-center gap-1">
                <div className="font-bold text-primary font-cabinet text-base">
                  Med900
                </div>
                <div className="text-xs bg-primary/10 text-primary px-2.5 py-0.5 rounded-full font-mono font-medium">
                  LAUNCH
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const delay = 0.12 + i * 0.08;
            return (
              <tr
                key={row.feature}
                className={`border-t border-border ${
                  row.highlight ? "bg-primary/5" : i % 2 === 0 ? "bg-muted/30" : ""
                }`}
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? "none" : "translateX(-30px)",
                  transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
                  transitionDelay: inView ? `${delay}s` : "0s",
                }}
              >
                <td className={`py-3.5 px-6 text-sm font-medium font-plex ${row.highlight ? "font-bold" : ""}`}>
                  {row.feature}
                </td>
                <td className="py-3.5 px-6 text-sm text-muted-foreground font-plex">
                  {row.detail}
                </td>
                <td className="py-3.5 px-6 text-center">
                  <span
                    style={{
                      display: "inline-flex",
                      opacity: inView ? 1 : 0,
                      transform: inView ? "scale(1)" : "scale(0.3)",
                      transition:
                        "opacity 0.4s ease, transform 0.4s cubic-bezier(.34,1.56,.64,1)",
                      transitionDelay: inView ? `${delay + 0.25}s` : "0s",
                    }}
                  >
                    <CheckCircle2 className="h-5 w-5 text-success mx-auto" />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ComparisonSection({
  stats,
  showCta = true,
  sectionId = "compare",
  showIntro = true,
}) {
  return (
    <section
      {...(sectionId ? { id: sectionId } : {})}
      className="py-24 px-4 relative overflow-hidden"
      data-testid="included-section"
      style={{
        background:
          "linear-gradient(180deg, hsl(220 25% 97%) 0%, hsl(220 15% 95%) 50%, hsl(220 20% 97%) 100%)",
      }}
    >
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, hsla(221, 100%, 50%, 0.09) 0%, transparent 60%)",
        }}
      />
      <div className="max-w-4xl mx-auto relative">
        {showIntro && (
          <Reveal>
            <div className="text-center mb-12">
              <div className="text-xs font-mono font-medium text-primary uppercase tracking-widest mb-3">
                WHAT'S INCLUDED
              </div>
              <h2 className="text-3xl sm:text-4xl font-cabinet font-black tracking-tight mb-4">
                Everything you need for focused UCAT prep
              </h2>
              <p className="text-lg text-muted-foreground font-plex">
                A clean launch product: realistic practice, public study guides,
                and analytics that tell you what to fix next.
              </p>
            </div>
          </Reveal>
        )}
        <Reveal delay={0.1}>
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <IncludedTable stats={stats} />
            {showCta && (
              <div className="p-6 border-t border-border bg-primary/5 text-center">
                <Link to="/signup">
                  <Button
                    size="lg"
                    className="text-base font-semibold font-cabinet rounded-full group"
                    data-testid="included-cta"
                  >
                    Start Practising{" "}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
